"use client";

import React, { useState, useEffect } from "react";
import { RadioGroup } from "../radio-button";
import Close from "../svg/close";
import InputField from "../input";
import User from "../svg/user";
import PhoneDef from "../svg/phone-def";
import CalendarInput from "../calendar-input";
import CalendarIcon from "../svg/calendar";
import TimeIcon from "../svg/timeicon";
import TimeInput from "../time-input";
import Checkbox from "../checkbox";
import Button from "../button";
import IconPhoneBlack from "../svg/phoneBlack";
import ThankYouModal from "../thank-you";
import Loader from "../svg/loader";

interface CallRequestFormProps {
    onClose: () => void;
    lang?: string;
}

const CallRequestForm: React.FC<CallRequestFormProps> = ({ onClose, lang }) => {
    const [formData, setFormData] = useState({
        serviceType: "",
        fullName: "",
        phoneNumber: "",
        date: null as Date | null,
        timeFrom: null as Date | null,
        timeTo: null as Date | null,
    });

    const [isChecked, setIsChecked] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const handleChange = (name: string, value: string | Date | null) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCheckboxChange = (checked: boolean) => {
        setIsChecked(checked);
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.serviceType) newErrors.serviceType = lang === 'en' ? 'Please select a service type' : 'Vänligen välj en tjänstetyp'
        if (!formData.fullName) newErrors.fullName = lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt'
        if (!formData.phoneNumber) newErrors.phoneNumber = lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt'
        if (!formData.date) newErrors.date = lang === 'en' ? 'Please select a date' : 'Vänligen välj ett datum'
        if (!formData.timeFrom) newErrors.timeFrom =  lang === 'en' ? 'Please select start time' : 'Vänligen välj starttid'
        if (!formData.timeTo) newErrors.timeTo = lang === 'en' ? 'Please select end time' : 'Vänligen välj sluttid'
        if (!isChecked) newErrors.checkbox = lang === 'en' ? 'Agreement to terms and conditions is required' : 'Godkännande av villkor krävs'

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        console.log('Starting form submission process...');
        console.log('Form data before validation:', formData);

        if (!validateForm()) {
            console.warn('Form validation failed');
            return;
        }

        console.log('Form validation passed');
        
        setIsLoading(true);
        setSubmitError(null);

        try {
            const requestBody = {
                formType: 'callback',
                formData: {
                    serviceType: formData.serviceType,
                    fullName: formData.fullName,
                    phone: formData.phoneNumber,
                    date: formData.date?.toISOString(),
                    timeFrom: formData.timeFrom?.toISOString(),
                    timeTo: formData.timeTo?.toISOString()
                }
            };

            console.log('Preparing API request:', {
                url: '/api/send-email',
                method: 'POST',
                body: requestBody,
                timestamp: new Date().toISOString()
            });

            const response = await fetch('/api/send-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            console.log('Response received:', {
                status: response.status,
                statusText: response.statusText,
                ok: response.ok,
                headers: Object.fromEntries(response.headers.entries())
            });

            const result = await response.json();
            console.log('Response JSON:', result);

            if (!response.ok || !result.success) {
                console.error('API returned error:', {
                    httpStatus: response.status,
                    httpStatusText: response.statusText,
                    apiSuccess: result.success,
                    apiError: result.error,
                    fullResult: result
                });
                
                throw new Error(result.error || lang === 'en' ? 'Failed to submit form' : 'Misslyckades med att skicka in formuläret');
            }

            console.log('Form submitted successfully!');
            console.log('Resetting form state...');
            
            setIsSubmitted(true);
            setFormData({
                serviceType: "",
                fullName: "",
                phoneNumber: "",
                date: null,
                timeFrom: null,
                timeTo: null,
            });
            setIsChecked(false);
            
            console.log('Form reset completed');

        } catch (error) {
            console.error('Form submission failed:', {
                error: error instanceof Error ? error.message : 'Unknown error',
                errorType: error instanceof Error ? error.constructor.name : 'Unknown',
                stack: error instanceof Error ? error.stack : 'No stack trace',
                timestamp: new Date().toISOString(),
                userLanguage: lang
            });
            
            setSubmitError(error instanceof Error ? error.message : lang === 'en' ? 'An unknown error occurred' : 'Ett okänt fel uppstod');
            
            // Дополнительное логирование для разных типов ошибок
            if (error instanceof TypeError && error.message.includes('fetch')) {
                console.error('Network error detected - check internet connection or CORS');
            }
            
        } finally {
            console.log('Form submission process finished, loading:', false);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isSubmitted) {
            setTimeout(() => setIsVisible(true), 50);
            
            setTimeout(() => {
                setIsVisible(false);
                setTimeout(() => {
                    setIsSubmitted(false);
                    onClose();
                }, 500);
            }, 3000);
        }
    }, [isSubmitted, onClose]);

    return (
        <div className="w-screen h-screen flex justify-center items-center">
            <div className={`max-w-[744px] rounded-[40px] max-[767px]:p-[20px] max-[767px]:rounded-[0] max-[767px]:overflow-hidden max-[767px]:h-[98vh] max-[767px]:w-[98%] border border-violet-50 shadow-lg p-[48px] space-y-[40px] bg-white relative duration-500 ease-in-out ${isSubmitted ? "opacity-0" : "opacity-1"}`}>
                <div className="h-full">
                    <div className="flex justify-between items-center max-[767px]:pb-[24px] mb-[40px] max-[576px]:mb-[0]">
                        <h2 className="font-bold text-violet-500 text-[32px] max-[576px]:text-[20px]">{lang === 'en' ? 'Call back request' : 'Ring tillbaka begäran'}</h2>
                        <div className="cursor-pointer mobicon" onClick={onClose}>
                            <Close />
                        </div>
                    </div>
                    <form onSubmit={handleSubmit} className="max-[767px]:overflow-auto max-[767px]:h-[100vh] relative max-[767px]:pb-[220px]">
                        <p className="text-violet-500 mb-[8px] max-[576px]:text-[12px] uppercase">{lang === 'en' ? 'Please select a preferred call time' : 'vänligen välj typ av önskad tjänst'}</p>
                        <RadioGroup
                            name="services"
                            options={[
                                { id: "Privatemove", label: lang === 'en' ? 'Private move' : 'Privat flytt', value: lang === 'en' ? 'Private move' : 'Privat flytt' },
                                { id: "Companyrelocation", label: lang === 'en' ? 'Company relocation' : 'Företagsflytt', value: lang === 'en' ? 'Company relocation' : 'Företagsflytt' }
                            ]}
                            selectedValue={formData.serviceType}
                            onChange={(value) => handleChange("serviceType", value)}
                        />
                        {errors.serviceType && <p className="text-red-500 text-sm mt-1">{errors.serviceType}</p>}

                        <div className="grid grid-cols-2 gap-6 max-[767px]:grid-cols-1 mt-[40px] max-[576px]:mt-[24px]">
                            <InputField 
                                label={lang === 'en' ? 'Full Name' : 'fullständigt namn'}
                                iconLeft={<User />} 
                                placeholder={lang === 'en' ? 'Write your full name' : 'Fullständigt namn'}
                                value={formData.fullName} 
                                onChange={(e) => handleChange("fullName", e.target.value)}
                                errorMessage={errors.fullName}
                            />
                            <InputField 
                                label={lang === 'en' ? 'Phone Number' : 'telefonnummer'} 
                                iconLeft={<PhoneDef />} 
                                placeholder={lang === 'en' ? 'Write your phone number' : 'Skriv ditt telefonnummer'} 
                                type="number"
                                value={formData.phoneNumber} 
                                onChange={(e) => handleChange("phoneNumber", e.target.value)}
                                errorMessage={errors.phoneNumber}
                            />
                        </div>

                        <div className="mt-[40px] mb-[24px] max-[576px]:mt-[24px]">
                            <p className="text-violet-500 mb-[8px] uppercase">{lang === 'en' ? 'Please select a preferred call time' : 'skriv när du vill få ett samtal från oss'} </p>
                            <div className="grid grid-cols-2 gap-6 max-[767px]:grid-cols-1">
                                <CalendarInput 
                                    label={lang === 'en' ? 'Date' : 'datum'}
                                    icon={<CalendarIcon />} 
                                    placeholder="YYYY/MM/DD" 
                                    value={formData.date} 
                                    onChange={(date) => handleChange("date", date)}
                                />
                                {errors.date && <p className="text-red-500 text-sm col-span-2">{errors.date}</p>}
                                
                                <div className="grid grid-cols-2 gap-6">
                                    <TimeInput 
                                        label={lang === 'en' ? 'From' : 'från'}
                                        icon={<TimeIcon />} 
                                        placeholder="00:00" 
                                        value={formData.timeFrom} 
                                        onChange={(time) => handleChange("timeFrom", time)}
                                    />
                                    <TimeInput 
                                        label={lang === 'en' ? 'To' : 'till'}
                                        icon={<TimeIcon />} 
                                        placeholder="00:00" 
                                        value={formData.timeTo} 
                                        onChange={(time) => handleChange("timeTo", time)}
                                    />
                                </div>
                                {(errors.timeFrom || errors.timeTo) && (
                                    <p className="text-red-500 text-sm col-span-2">
                                        {errors.timeFrom || errors.timeTo}
                                    </p>
                                )}
                            </div>
                        </div>

                        <Checkbox
                            label={lang === 'en' ? 'I consent to my personal data being processed in accordance with the' : 'Jag samtycker till att mina personuppgifter behandlas i enlighet med'}
                            required={true}
                            checked={isChecked}
                            onChange={handleCheckboxChange}
                            link={`${lang === 'en' ? '/en' : ''}/privacy-policy`}
                            linktext={lang === 'en' ? 'privacy policy' : 'integritetspolicyn'}
                        />
                        {errors.checkbox && <p className="text-red-500 text-sm mt-1">{errors.checkbox}</p>}

                        {submitError && (
                            <div className="p-4 mb-4 mt-4 text-red-700 bg-red-100 rounded-lg">
                                {submitError}
                            </div>
                        )}

                        <div className="flex justify-end mt-[40px] max-[767px]:fixed max-[767px]:z-20 max-[767px]:bottom-[16px] max-[767px]:left-[50%] max-[767px]:translate-x-[-50%] max-[576px]:w-[90%]">
                            <Button 
                                text={isLoading ? lang === 'en' ? 'Sending...' : 'Sändning...' : lang === 'en' ? 'REQUEST A CALL' : 'bli uppringd'}
                                icon={isLoading ? <Loader /> : <IconPhoneBlack />} 
                                type="primary" 
                                mobileFull="yes"
                            />
                        </div>
                    </form>
                </div>
            </div>

            {isSubmitted && (
                <div className={`fixed inset-0 flex items-center justify-center bg-[#00000080] z-10 transition-opacity duration-500 ease-in-out 
                    ${isVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                    <div className={`transform fixed transition-transform duration-500 ease-in-out 
                        ${isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
                        <ThankYouModal title={lang === 'en' ? 'Thank you!' : 'Tack!'} text={lang === 'en' ? 'Your call request has been submitted successfully.' : 'Din begäran om återuppringning har skickats. Vår operatör kommer att ringa upp dig på utsatt tid.'} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CallRequestForm;